// @ts-nocheck
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';

import type { TransformedPointOfSaleReport } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';

import type { PreviousReportsLinks } from '../../../report-shared/components/PreviousReportsButton/PreviousReportsButton';
import { ReportComplianceFooter } from '../../../report-shared/components/ReportComplianceFooter';
import { BaseReport } from '../../../reports/BaseReport';
import { usePointOfSaleReportSections } from '../../hooks/usePointOfSaleReportSections/usePointOfSaleReportSections';

type PointOfSaleReportProps = {
  report: TransformedPointOfSaleReport & {
    monitoringStatus?: string | null;
    changesSummary?: string | null;
    isAlert?: boolean | null;
    comparedToReportId?: string | null;
  };
  reportType?: string;
  customerFeatures?: CustomerFeatures | null;
  disableSectionObserver?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  header?: ReactNode;
  googleMapsApiKey?: string;
  previousReportsLinks?: PreviousReportsLinks | null;
  locale?: string;
  Link: React.ComponentType<{
    to: string;
    children: React.ReactNode;
    className?: string;
  }>;
  BusinessDetailsSlot?: React.ComponentType;
  /** Rendered in the right rail below the section list (BAL-8047 connected-tasks panel). */
  rightRailSlot?: ReactNode;
};

export const PointOfSaleReport = forwardRef<HTMLDivElement, PointOfSaleReportProps>(
  (
    {
      report,
      reportType,
      customerFeatures,
      disableSectionObserver = false,
      FeedbackComponent,
      header,
      googleMapsApiKey,
      previousReportsLinks,
      locale,
      Link,
      BusinessDetailsSlot,
      rightRailSlot,
    },
    ref,
  ) => {
    const computedPreviousReportsLinks = useMemo(() => {
      if (previousReportsLinks) {
        return previousReportsLinks;
      }

      const comparedToReportId = (report as any).comparedToReportId;

      if (!comparedToReportId) {
        return null;
      }

      return {
        previousMonitoringReportHref: `/point-of-sale/${comparedToReportId}`,
        onboardingReportHref: null,
      };
    }, [report, previousReportsLinks]);

    const { sections } = usePointOfSaleReportSections(
      report,
      googleMapsApiKey,
      FeedbackComponent,
      Link,
      reportType,
      computedPreviousReportsLinks,
      customerFeatures,
      locale,
      BusinessDetailsSlot,
    );

    return (
      <BaseReport
        ref={ref}
        sections={sections}
        disableSectionObserver={disableSectionObserver}
        header={header}
        footer={<ReportComplianceFooter />}
        rightRailSlot={rightRailSlot}
      />
    );
  },
);

PointOfSaleReport.displayName = 'PointOfSaleReport';
